import React, { useRef, useState } from "react";
import { Pencil, Eye, EyeOff } from "lucide-react";

interface ProfileCardProps {
  profilePic: string;
  profileEditMode: boolean;
  profileName: string;
  setProfileName: (v: string) => void;
  profilePosition: string;
  profileEmail: string;
  getPositionColor: (pos: string) => string;
  setProfileEditMode: (v: boolean) => void;
  handleSaveProfile: () => void;
  handleProfilePicChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profilePic,
  profileEditMode,
  profileName,
  setProfileName,
  profilePosition,
  profileEmail,
  getPositionColor,
  setProfileEditMode,
  handleSaveProfile,
  handleProfilePicChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Change Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handleChangePassword = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    // Change password logic here (API call, etc)
    setShowPasswordModal(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    alert("Password changed successfully!");
  };

  return (
    <>
      <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl p-6 shadow-lg mb-6 flex items-center gap-6">
        <div className="relative group">
          <img
            src={profilePic}
            alt="Profile"
            className="w-20 h-20 rounded-full object-cover border-4 border-blue-500"
          />
          {profileEditMode && (
            <>
              <span
                className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition"
                style={{ background: "rgba(128,128,128,0.25)" }}
                onClick={() => fileInputRef.current?.click()}
                title="Change Profile Picture"
                role="button"
                tabIndex={0}
              >
                <Pencil className="h-7 w-7 text-gray-700" />
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePicChange}
              />
            </>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {profileEditMode ? (
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="text-xl font-bold text-gray-800 border-b border-blue-300 focus:outline-none focus:border-blue-500 bg-transparent"
              />
            ) : (
              <span className="text-xl font-bold text-gray-800">
                {profileName}
              </span>
            )}
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getPositionColor(
                profilePosition
              )}`}
            >
              {profilePosition}
            </span>
          </div>
          <div className="text-gray-600 text-sm mb-2">{profileEmail}</div>
          <div className="flex gap-2">
            {profileEditMode ? (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                onClick={handleSaveProfile}
              >
                Save
              </button>
            ) : (
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                onClick={() => setProfileEditMode(true)}
              >
                Edit Profile
              </button>
            )}
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
              onClick={() => setShowPasswordModal(true)}
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-8 shadow-lg w-full max-w-xs galaxy-modal-popin">
            <h2 className="text-lg font-bold mb-4 text-gray-800">
              Change Password
            </h2>
            <div className="relative mb-3">
              <input
                type={showOldPassword ? "text" : "password"}
                placeholder="Old Password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                onClick={() => setShowOldPassword((v) => !v)}
              >
                {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative mb-3">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                onClick={() => setShowNewPassword((v) => !v)}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative mb-3">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword((v) => !v)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordError && (
              <div className="text-red-500 text-sm mb-2">{passwordError}</div>
            )}
            <div className="flex gap-2">
              <button
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={handleChangePassword}
              >
                Save
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                onClick={() => {
                  setShowPasswordModal(false);
                  setOldPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  setPasswordError("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileCard;
